import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Car, MessagesSquare, LayoutDashboard, Columns2, RotateCcw, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { salesDeskAPI } from '../lib/api';
import { demoSlot } from '../lib/session';
import { clearGuidedTourSeen } from './shared/GuidedTour';
import LanguageToggle from './LanguageToggle';

// Live 3-way persona switcher for the automotive demo (auto-demo-org-0001).
// Direct-jump switcher (not a sequential toggle): all three personas stay
// visible/clickable, the active one is highlighted. Navigation happens in the
// SAME tab via relative paths and reflects REAL shared demo state.
// Visible only on the three automotive demo routes; on those routes the legacy
// AutoShowroomSwitcher hides (see shouldShowAutoDemoSwitcher).

// Pure visibility predicate — shared with AutoShowroomSwitcher so the two
// bottom-center controls never render simultaneously.
export function shouldShowAutoDemoSwitcher(pathname, search) {
    if (typeof window === 'undefined') return false;
    // Never INSIDE a split-screen pane: the pill would render once per pane and
    // switching persona inside a pane would leave the split view half-broken.
    // The switcher on the outer split page is the one that stays in charge.
    if (demoSlot()) return false;
    const autoDemo = sessionStorage.getItem('yulontap_auto_demo') === '1';
    if (pathname === '/demo/automotive/split') return true;
    if (pathname.startsWith('/vehicle/') && pathname !== '/vehicle/compare') return autoDemo;
    if (pathname === '/vehicle-leads' || pathname === '/sales-desk') return autoDemo;
    if (pathname === '/command-center') {
        const params = new URLSearchParams(search || '');
        return params.get('org') === 'auto-demo-org-0001';
    }
    return false;
}

export default function AutoDemoSwitcher() {
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [resetting, setResetting] = useState(false);
    const visible = shouldShowAutoDemoSwitcher(pathname, search);

    // The pill is fixed to the bottom of the viewport, so without this the last
    // row of any scrolling page sits permanently underneath it.
    useEffect(() => {
        if (!visible) return;
        const prev = document.body.style.paddingBottom;
        document.body.style.paddingBottom = '76px';
        return () => { document.body.style.paddingBottom = prev; };
    }, [visible]);

    if (!visible) return null;

    const doReset = async () => {
        if (resetting) return;
        if (!window.confirm(t('demoAuto.resetConfirm', 'Reset the demo? This clears all inquiries, test drives and alerts created during demos. Inventory and promotions are kept.'))) return;
        setResetting(true);
        try {
            await salesDeskAPI.resetDemo();
            // The reset deletes the demo's requests server-side, so any buyer
            // thread id still remembered in THIS browser now points at nothing.
            // Left behind, the next customer message is posted into a 404 and
            // never reaches the Sales Desk or the Command Center.
            Object.keys(localStorage)
                .filter((k) => k.startsWith('yot_conv_') || k.startsWith('yot_ins_conv_'))
                .forEach((k) => localStorage.removeItem(k));
            // A fresh demo should feel like a first visit: replay the guided tour.
            clearGuidedTourSeen();
            window.location.reload();
        }
        catch { setResetting(false); }
    };

    let active = null;
    if (pathname === '/demo/automotive/split') active = 'split';
    else if (pathname.startsWith('/vehicle/')) active = 'customer';
    else if (pathname === '/vehicle-leads' || pathname === '/sales-desk') active = 'sales';
    else if (pathname === '/command-center') active = 'manager';

    const go = (persona) => {
        // Always route through the persona's demo entry so the CORRECT role
        // session is minted. Reusing the current token across personas (e.g.
        // a salesperson token loading the manager Command Center) caused a 403.
        if (persona === 'customer') { navigate('/demo/automotive/buyer'); return; }
        if (persona === 'sales') { navigate('/demo/automotive/salesperson'); return; }
        if (persona === 'manager') { navigate('/demo/automotive/command-center'); return; }
        // Both at once: the real buyer page and the real staff page, side by side.
        if (persona === 'split') { navigate('/demo/automotive/split'); }
    };

    const items = [
        { key: 'customer', icon: <Car className="w-4 h-4" />, label: t('demoAuto.personaCustomer', 'Customer'), aria: t('demoAuto.viewAsCustomer', 'View as Customer'), grad: 'from-emerald-500 to-green-600', ring: 'ring-emerald-300', tid: 'auto-demo-switch-customer' },
        { key: 'sales', icon: <MessagesSquare className="w-4 h-4" />, label: t('demoAuto.personaSales', 'Sales'), aria: t('demoAuto.viewAsSales', 'View as Sales'), grad: 'from-blue-500 to-indigo-600', ring: 'ring-blue-300', tid: 'auto-demo-switch-sales' },
        { key: 'manager', icon: <LayoutDashboard className="w-4 h-4" />, label: t('demoAuto.personaManager', 'Manager'), aria: t('demoAuto.viewAsManager', 'View as Manager'), grad: 'from-orange-500 to-amber-600', ring: 'ring-orange-300', tid: 'auto-demo-switch-manager' },
        { key: 'split', icon: <Columns2 className="w-4 h-4" />, label: t('demoAuto.personaSplit', 'Split Screen'), aria: t('demoAuto.viewSplitScreen', 'View buyer and dealership side by side'), grad: 'from-fuchsia-500 to-purple-600', ring: 'ring-fuchsia-300', tid: 'auto-demo-switch-split' },
    ];

    return (
        <div
            role="group"
            aria-label={t('demoAuto.switchAria', 'Switch demo perspective')}
            data-testid="auto-demo-switcher"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-nowrap items-center gap-1.5 rounded-full border border-white/15 bg-black/70 backdrop-blur px-1.5 py-1.5 shadow-xl max-w-[calc(100vw-1rem)] overflow-x-auto no-scrollbar">
            <span className="px-2 shrink-0 text-[10px] font-semibold uppercase tracking-widest text-white/70 hidden sm:inline">
                {t('demoAuto.demoBadge', 'Live Demo')}
            </span>
            {items.map((it) => {
                const isActive = active === it.key;
                return (
                    <button
                        key={it.key}
                        onClick={() => go(it.key)}
                        aria-label={it.aria}
                        aria-current={isActive ? 'true' : undefined}
                        title={isActive ? t('demoAuto.currentView', 'Current view') : it.aria}
                        data-testid={it.tid}
                        className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-white border-0 bg-gradient-to-r ${it.grad} transition-transform duration-200 hover:opacity-90 ${isActive ? `ring-2 ${it.ring} ring-offset-2 ring-offset-black scale-105 shadow-lg` : 'opacity-90 hover:scale-105'}`}>
                        {it.icon}<span className="hidden sm:inline">{it.label}</span>
                    </button>
                );
            })}
            <span className="w-px h-5 shrink-0 bg-white/15 mx-0.5" aria-hidden="true" />
            <div className="shrink-0 w-[150px]" data-testid="demo-language-picker">
                <LanguageToggle variant="compact" surface="demo" />
            </div>
            <span className="w-px h-5 shrink-0 bg-white/15 mx-0.5" aria-hidden="true" />
            <button
                onClick={doReset}
                disabled={resetting}
                aria-label={t('demoAuto.resetDemo', 'Reset demo')}
                title={t('demoAuto.resetDemo', 'Reset demo')}
                data-testid="auto-demo-reset"
                className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-200 border border-white/15 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50">
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                <span className="hidden sm:inline">{t('demoAuto.resetDemo', 'Reset demo')}</span>
            </button>
        </div>
    );
}
